import {
  CAMERA_FRAMING_OPTIONS,
  CAMERA_LENS_OPTIONS,
  CAMERA_MOVEMENT_OPTIONS,
} from './directorPresets/cameraSlotOptions.js';
import { BLOCKING_POSITIONS } from './directorPresets/directionSlotOptions.js';

/**
 * The one description of what an enhanced SHOT is, shared by every surface that asks for one.
 *
 * Extracted from the storyboard template after it was fixed, because the fix is not
 * storyboard-specific and copying it into seventeen video configs would create seventeen
 * places for it to drift. James, 2026-08-23: "single source of truth as much as possible",
 * and per-model flavour "downstream of the source of truth" -- which is why a surface
 * contributes a guidance paragraph and a temperature, and nothing else.
 *
 * Every rule in here was measured, not reasoned:
 *
 *  - ORDER IS THE CONTRACT. With appearance and style framed as material and placed first,
 *    the model described the SET and deleted both named characters -- 0/3 runs against 3/3
 *    without any context at all. The user's line named as THE SHOT and put first, cast as an
 *    obligation, style demoted to a treatment, is what returned it to 3/3. Reword these
 *    blocks freely; do not reorder them without re-running the live matrix.
 *  - THE MENUS ARE THE MENUS. Given prose examples the model answered "static locked-off",
 *    which is not a catalog value, so the chip rendered a correct-looking value its own
 *    picker could not select. Handing over the real option strings fixed it.
 *  - THE QUIET CHARACTER IS THE HARD ONE. Banning "neutral" did not stop the model using it
 *    on the character it had least to say about -- 2/3 runs with four in the shot. Saying
 *    what to write INSTEAD is the half that was missing. Confirmed 0/3 across all ten live
 *    scenarios on 2026-08-23.
 *
 * A surface only asks for the slots it can STORE. A single clip keeps `camera.framing` and
 * `camera.movement` and nothing else (`singleClipCamera.js`), so offering it a lens would
 * produce a value with nowhere to live -- a chip that vanishes on the next write.
 *
 * THE ASSEMBLED TEXT IS PINNED. `storyboardShotPromptGolden.test.js` holds the storyboard's
 * whole system prompt byte for byte, because a wording change here is a behavioural change
 * that no other test in this repo can see. Change it deliberately, and re-run the paid matrix.
 */

/** Slots a surface can offer, in the order the chips render. */
export const SHOT_CONTRACT_SLOTS = Object.freeze({
  framing: { field: 'framing', options: CAMERA_FRAMING_OPTIONS, label: 'framing' },
  cameraMovement: { field: 'cameraMovement', options: CAMERA_MOVEMENT_OPTIONS, label: 'cameraMovement' },
  lens: { field: 'lens', options: CAMERA_LENS_OPTIONS, label: 'lens' },
});

/** Free-prose fields, which have no closed menu to choose from. */
export const SHOT_CONTRACT_PROSE_FIELDS = Object.freeze(['blocking', 'device']);

/** `"framing", "cameraMovement" and "lens"` -- read aloud, so the rule reads as English. */
function quotedList(names) {
  const quoted = names.map((name) => `"${name}"`);
  if (quoted.length <= 1) return quoted.join('');
  return `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
}

/**
 * The line that stops the model describing the set instead of the scene. FIRST, always.
 */
export function buildShotPrimacyPreamble() {
  return "THE SHOT is the user's line below. Whatever it says happens, happens, and whoever it "
    + 'names is in the picture doing it. Everything after it is reference for HOW that looks -- '
    + 'never a substitute for it. Never return a description of an empty room or of a lone '
    + 'object when the line names people.';
}

/**
 * Who is in the shot, as an obligation. The handles are a wire format for the reply's keys;
 * they leaked into prose 3/3 runs when a character had a role but no bound asset, so the
 * instruction says plainly that they are never to be written.
 */
export function buildCastBlock(castTokens = [], { withTokens = true } = {}) {
  if (!castTokens.length) return '';
  const label = (entry) => entry.role || entry.name || entry.assetId;
  const lines = castTokens
    .map((entry) => (withTokens ? `  ${entry.token} = ${label(entry)}` : `  ${label(entry)}`))
    .join('\n');
  const block = `\n\nWHO IS IN THIS SHOT -- every one of them appears in the image, by name:\n${lines}`;
  // The handle-warning sentence exists ONLY because the handles are in the text above it. A
  // surface that stores no per-character reply is given the cast by name and never sees a
  // code, so repeating the warning there would name a convention the model was not shown --
  // and telling a model not to write something it has not been given is how it learns the
  // string. The single clip is that surface: `videoParams.camera` has nowhere to put an
  // acting note, so it asks for none and gets no tokens.
  if (!withTokens) return block;
  return block
    + '\nThose codes are labels for the reply\'s "performances" keys ONLY. Never write C1, C2 or'
    + ' any of them in the prose -- call each character by their name or their role.';
}

/** Appearance reference, explicitly subordinate to the action. */
export function buildAppearanceBlock(elementContext) {
  if (!elementContext) return '';
  return `\n\nWHAT THEY LOOK LIKE (appearance reference only -- this never replaces the action):\n  ${elementContext}`;
}

/** Style as a treatment. As a headline it was recited verbatim as the opening clause. */
export function buildTreatmentBlock(genreDirective) {
  if (!genreDirective) return '';
  return `\n\nRENDERING TREATMENT (how it is lit and graded, NOT what is in it): ${genreDirective}`;
}

/** How long the shot runs, when the surface knows. */
export function buildDurationNote(shotDuration) {
  if (!Number.isFinite(shotDuration)) return '';
  return `\nThis shot runs ${shotDuration} seconds.`;
}

/** The actual pickable values, so a returned one can be seen selected and swapped. */
export function buildMenuBlock(slotNames = []) {
  const slots = slotNames.map((name) => SHOT_CONTRACT_SLOTS[name]).filter(Boolean);
  if (!slots.length) return '';
  const lines = slots
    .map((slot) => `  ${slot.label}: ${slot.options.map((option) => `"${option.id}"`).join(', ')}`)
    .join('\n');
  return '\n\nCHOOSE FROM THESE EXACT STRINGS -- they are the menus the user sees, and a value '
    + `outside them cannot be selected:\n${lines}`;
}

/** One note per character, and what to do with the character who has least to do. */
export function buildActingNotesBlock(castTokens = []) {
  if (!castTokens.length) return '';
  const tokens = castTokens.map((entry) => entry.token);
  return '\n\nACTING NOTES -- required, one for EVERY character listed above, keyed by token '
    + `(${tokens.join(', ')}), for example {"${tokens[0]}":"holds the stare a beat too long"}. `
    + 'A note is direction for the performer: what the face, eyes, hands, breath or attention '
    + 'are doing. ONE short phrase, about a dozen words -- it becomes a small control the user '
    + 'glances at, not a paragraph. Never a restatement of the action. A character with little '
    + 'to do in this shot is the HARDEST one and still gets a real note: what their hands are '
    + 'doing while they wait, where their attention has drifted, what they are holding back. '
    + '"Neutral", "expressionless" and "impassive" describe no performance at all and are rejected.';
}

/**
 * Who each character usually is, so the required note is written IN character rather than
 * over it: a note outranks the inherited line, so this is the only place left that carries
 * it. The departure clause is James's ruling (2026-09-09), not hedging: a climax should not
 * need a hand-typed note to break character.
 */
export function buildBaselinesBlock(baselines = []) {
  if (!baselines.length) return '';
  const lines = baselines.map(({ token, line }) => `  ${token}: ${line}`).join('\n');
  return '\n\nBASELINES -- how each of these people usually carries themselves, and what this '
    + 'place does to them. Write their note in that character unless THE SHOT clearly calls for '
    + `a departure, in which case the departure wins:\n${lines}`;
}

/** The reply shape, listing only the fields this surface asked for. */
export function buildReplyShape({
  slotNames = [], proseFields = [], withPerformances = false, withBlockingVariants = false,
} = {}) {
  const fields = ['"prompt":"string"']
    .concat(slotNames.filter((name) => SHOT_CONTRACT_SLOTS[name]).map((name) => `"${name}":"string"`))
    .concat(proseFields.map((name) => `"${name}":"string"`))
    // OPT-IN, and the storyboard and sequence surfaces deliberately do not opt in: their
    // manifests cannot store variants, and ask-only-what-you-store is what keeps a returned
    // value from having nowhere to live. Their goldens must stay byte-identical.
    .concat(withBlockingVariants
      ? [`"blockingVariants":{${BLOCKING_POSITIONS.map((p) => `"${p}":"string"`).join(',')}}`,
        '"deviceVariants":["string","string","string","string"]']
      : [])
    .concat(withPerformances ? ['"performances":{"TOKEN":"string"}'] : []);
  return `\n\nReturn strict JSON only, using this exact shape:\n{${fields.join(',')}}`;
}

/**
 * The rules that apply to every surface, phrased once.
 *
 * The line breaks inside these strings are part of the assembled prompt and are pinned by the
 * golden. They are not source formatting.
 */
export function buildShotContractRules({
  slotNames = [], proseFields = [], unit = 'image', extraRules = '', withBlockingVariants = false,
} = {}) {
  const menuFields = slotNames.filter((name) => SHOT_CONTRACT_SLOTS[name]);
  const menuRule = menuFields.length
    ? `\n- ${quotedList(menuFields)} must each be copied EXACTLY from the menus`
      + '\n  above, or be "inherit". Do not paraphrase one and do not invent a shorter way of'
      + '\n  saying it -- a near-miss reads correctly and is unselectable, which is worse than'
      + '\n  "inherit" because the user cannot tell the difference by looking.'
    : '';
  // PLACEMENT ONLY, stated as a prohibition rather than a preference because the measured
  // failure was the model spending the field on description: 16 sampled replies shared a
  // MEDIAN 45% of blocking's content words with the prose beside it, worst case 69% -- one
  // re-described "golden retriever" and "path" the prose had already established. A chip
  // that repeats the sentence next to it is noise, and it is noise the compiled prompt pays
  // for twice, since the chip and the prose are concatenated into one request.
  const blockingRule = proseFields.includes('blocking')
    ? '\n- "blocking" is PLACEMENT ONLY: where each subject sits in the frame and how they'
      + '\n  move through it. Name each subject in the fewest words that identify them, then'
      + '\n  give position and movement and STOP. No appearance, no colour, no wardrobe, no'
      + '\n  lighting, no weather, no describing the setting -- the prose already carries all'
      + '\n  of that, and this line sits beside it in the same prompt.'
      + '\n  WRONG: "Rufus, a black Labrador, trots down a narrow, mud-slicked path through a'
      + '\n  dense forest, stopping to turn his head" -- breed, colour, path and forest are'
      + '\n  all in the prose already.'
      + '\n  RIGHT: "Rufus frame-center, trotting away from camera, then stopping to look'
      + '\n  back over his shoulder."'
      + '\n  RIGHT: "the clerk frame-right foreground, the customer behind the counter."'
      + '\n  Use "inherit" if the shot does not call for one.'
    : '';
  const blockingVariantsRule = withBlockingVariants
    ? '\n- "blockingVariants" gives the SAME shot restaged at each of the four positions:'
      + '\n  one sentence per position, naming the same people and things the line names.'
      + '\n  Each is a complete alternative to "blocking", not a fragment, and stays ONE'
      + '\n  sentence.'
      + '\n  A different position means a DIFFERENT PATH through the frame, so the movement'
      + '\n  has to follow from where they start -- do not write one movement and paste it'
      + '\n  under all four positions. Four sentences that differ only in the position'
      + '\n  phrase are one option wearing four coats, and the menu is then worthless.'
      + '\n  WRONG: "...frame-left foreground, wading from left to right, head down" beside'
      + '\n  "...frame-right background, wading from left to right, head down" -- identical'
      + '\n  but for the position.'
      + '\n  RIGHT: "...frame-left foreground, wading toward camera until they fill frame"'
      + '\n  beside "...frame-right background, a distant shape drifting toward the far bank".'
    : '';
  // Device is prose with a generated menu too. Its four are not keyed by an axis the way
  // blocking's are, so DISTINCTNESS has to be asked for outright -- four rephrasings of one
  // idea is a menu with one entry wearing four coats.
  const deviceVariantsRule = withBlockingVariants
    ? '\n- "deviceVariants" gives FOUR alternative devices for this same shot, each a'
      + '\n  different idea rather than a rewording of the others -- vary what the device'
      + '\n  DOES (an angle, an obstruction, a reflection, a focus move, a held beat), not'
      + '\n  just its wording. Each is a complete alternative to "device", one short phrase,'
      + '\n  and none of them repeats the prose.'
    : '';
  const deviceRule = proseFields.includes('device')
    ? '\n- "device" is a single visual or narrative device the shot leans on, for example'
      + '\n  "reflection in the case glass", "rack focus to the door". Use "inherit" if the'
      + '\n  shot does not call for one.'
    : '';
  // A storyboard shot is a STILL and a clip MOVES, and the difference is load-bearing: told
  // to "describe ONE image", the single-clip configs were being asked for a frozen frame in
  // the same breath as their own guidance asked for a motion endpoint and a paced camera
  // move. Same rule, one word of it owned by the surface.
  const unitRule = unit === 'shot'
    ? '\n- Describe ONE continuous shot. Do not narrate a sequence of events and do not cut'
      + '\n  between setups.'
    : '\n- Describe ONE image. Do not narrate a sequence of events.';
  return '\n\nRules:'
    + unitRule
    + '\n- Keep every proper noun, breed, wardrobe and colour the user already wrote. Never'
    + '\n  re-specify a subject differently and never generalise it away.'
    + '\n- EVERY field you return is placed in the prompt right beside the prose, so the'
    + '\n  prose must NOT say it a second time. Return a cameraMovement and the prose does'
    + '\n  not describe the camera move; return a device and the prose does not describe'
    + '\n  that device; return framing or lens and the prose names neither the shot size nor'
    + '\n  the lens. Say each fact exactly ONCE, in its field, and never contradict a field'
    + '\n  in the prose.'
    + '\n  WRONG: device "ethereal moonlight dappling the forest path" returned while the'
    + '\n  prose says "shafts of pale, ethereal moonlight pierce the canopy, dappling the'
    + '\n  path" -- the same image billed twice.'
    + '\n  WRONG: cameraMovement "tracking shot following the subject" returned while the'
    + '\n  prose says "the camera, tracking shot following the subject as they move".'
    + menuRule + blockingRule + blockingVariantsRule + deviceRule + deviceVariantsRule
    + '\n- Every field you return must be something a director would ACTUALLY choose for'
    + '\n  this shot. Returning "inherit" is always better than inventing one to fill the'
    + '\n  slot -- an unwanted value is worse than an absent one, because it becomes a'
    + '\n  control the user then has to clear.'
    + extraRules
    + '\n- No shot numbers, no editing instructions, no commentary, no markdown.';
}

/**
 * The camera move the USER already picked, when they picked one.
 *
 * `cameraDirective` and the `cameraMovement` menu are the same vocabulary, not two:
 * `CAMERA_MOVEMENT_OPTIONS[].id` IS `preset.promptText`, which is exactly what
 * `getCameraPromptText` returns (`cameraSlotOptions.js:26`, `videoPromptDirector.js:32`).
 * Handing the model a menu to choose from while the user has already chosen would make the
 * model a second owner of a fact the user owns, and the losing writer is whichever one the
 * code happens to apply last. So the pick is stated as the answer, not as a suggestion.
 */
export function buildChosenMovementNote(cameraDirective) {
  if (!cameraDirective) return '';
  return `\n\nTHE USER HAS ALREADY CHOSEN THE CAMERA MOVE: "${cameraDirective}". Return exactly `
    + 'that string as "cameraMovement" and write the prose so the move is motivated. Do not '
    + 'substitute a different one.';
}

/** Motion quality, when a speed preset is set. */
export function buildMotionNote(speedSuffix) {
  if (!speedSuffix) return '';
  return `\n\nMOTION QUALITY: incorporate "${speedSuffix}".`;
}

/**
 * The whole body between the surface's headline and its reply shape, in the ONE order that
 * was measured. A surface passes what it has; every block renders empty when it has nothing.
 */
export function buildShotContractBody({
  referenceNote = '',
  castTokens = [],
  elementContext = null,
  genreDirective = null,
  shotDuration = null,
  slotNames = [],
  withPerformances = true,
  performanceBaselines = [],
} = {}) {
  return buildShotPrimacyPreamble()
    + referenceNote
    + buildCastBlock(castTokens, { withTokens: withPerformances })
    + buildAppearanceBlock(elementContext)
    + buildTreatmentBlock(genreDirective)
    + buildDurationNote(shotDuration)
    + buildMenuBlock(slotNames)
    + (withPerformances ? buildActingNotesBlock(castTokens) + buildBaselinesBlock(performanceBaselines) : '');
}
